import type { Dictionary } from "./id.ts";

/** Secondary language. Bahasa Indonesia is the product's first language, not this. */
export const en: Dictionary = {
  app: {
    name: "Pustaka Aceh",
    tagline: "Search across Aceh government documents",
    prototype: "Prototype",
  },
  nav: {
    studio: "AI Studio",
    collections: "Collections",
    sources: "Sources",
    library: "Library",
    trends: "Query trends",
    playground: "Playground",
    conversations: "Conversations",
    newConversation: "New conversation",
    newNotebook: "+ New notebook",
    notebooks: "Notebooks",
    signOut: "Sign out",
    back: "Back",
    skipToContent: "Skip to main content",
  },
  home: {
    notebooks: "My notebooks",
    newNotebook: "+ New notebook",
    untitled: "Untitled notebook",
    noNotebooks: "No notebooks yet. Create one to start choosing sources.",
    title: "Notebooks",
    subtitle: "Gather sources into a notebook, then ask about them.",
    studioTitle: "AI Studio",
    studioSub: "Ask a question. Every answer carries sources you can follow back.",
    studioFoot: "Start a conversation",
    collectionsTitle: "Collections",
    collectionsSub:
      "Group documents into collections, upload new files, and choose what gets searched.",
    collectionsFoot: "Manage collections",
    recent: "Recent conversations",
    noRecent: "No conversations yet.",
  },
  trends: {
    eyebrow: "Internal analytics",
    title: "What are people looking for?",
    subtitle:
      "New demand signals from the moment tracking was enabled—without replaying older conversation history.",
    summary: "Query trend summary",
    tracked: "Tracked queries",
    classified: "Classified",
    attributed: "Source-attributed",
    sourceEyebrow: "Source distribution",
    sources: "Most-used sources",
    categoryEyebrow: "Six fixed domains",
    categories: "Dominant topics",
    keywordEyebrow: "Demand language",
    keywords: "Top searched keywords",
    queryShare: "share of all queries",
    unclassified: "{{count}} unclassified",
    noSources: "No answer has been attributed to a source yet.",
    noCategories: "No category has been assigned successfully yet.",
    noKeywords: "No keywords are available yet.",
    emptyTitle: "Tracking has just begun",
    emptyBody: "New queries will appear here after their answers finish processing.",
    forwardOnly: "Forward-only tracking · no backfill",
    error: "The trend summary could not be loaded. Try again.",
    forbiddenTitle: "This dashboard is for operators",
    forbiddenBody: "Sign in as an admin or operator to view aggregate usage.",
  },
  playground: {
    eyebrow: "Admin tool · editorial prompt",
    title: "Test how an answer is written",
    subtitle:
      "Try one editorial instruction against one question, using the same agent and query-v2 checks as the main service.",
    boundaryTitle: "Only the editorial style changes",
    boundaryBody:
      "Tools, citations, source-support judges, output format, and the Government Policy Guard remain system-controlled.",
    forbiddenTitle: "This playground is for admins only",
    forbiddenBody: "Sign in as an admin to test an editorial prompt candidate.",
    form: {
      title: "Candidate and question",
      promptLabel: "Editorial prompt candidate",
      promptPlaceholder: "Example: Answer clearly, concisely, and professionally in Bahasa Indonesia.",
      promptHelp: "This instruction applies only to this single test run.",
      questionLabel: "Test question",
      questionPlaceholder: "Ask one question about Aceh government documents…",
      unpublishedHelp: "The test does not publish or activate this candidate.",
      submit: "Test prompt",
      running: "Testing…",
    },
    result: {
      title: "Test result",
      unpublished: "Not published",
      elapsed: "{{milliseconds}} ms",
      sources: "Answer citations",
      noSources: "This answer carries no document citation that can be displayed.",
      runningTitle: "Query-v2 is running",
      runningBody: "The agent is reading sources, checking each claim, and applying the Government Policy Guard.",
      emptyTitle: "No result yet",
      emptyBody: "Enter a candidate and one question, then run the test.",
      unsupportedSchemaTitle: "Result format is not supported",
      unsupportedSchemaBody:
        "The result is hidden because its annotations are not a recognized query-v2 format.",
    },
    errors: {
      title: "The test did not complete",
      validation: "Check the candidate and question. Both must contain valid text.",
      authentication: "The admin session has ended. Sign in again, then retry.",
      forbidden: "This account lacks admin access, or a requested source is not accessible.",
      sources: "The required sources were not found or are not available yet.",
      tooLarge: "The candidate or question is too long. Shorten it, then retry.",
      rateLimit: "Too many tests were submitted in a short time. Wait a moment, then retry.",
      backend: "The AI service could not complete this test. Try again later.",
      unknown: "An unexpected problem occurred. Try again.",
    },
  },
  login: {
    title: "Sign in",
    subtitle: "Interim accounts, until SSO is available.",
    username: "Username",
    password: "Password", // pragma: allowlist secret
    submit: "Sign in",
    submitting: "Checking…",
    promise:
      "Answers are built from indexed documents and carry their sources. Claims the source check does not support are marked, not hidden.",
    personas: "Prototype accounts",
    personasHelp: "Pick an account, then enter the password.",
    persona_admin: "Manage and delete collections",
    persona_operator: "Upload documents and watch processing",
    persona_pengguna: "Ask questions and see own conversations",
  },
  scope: {
    bar: "Scope",
    edit: "Change",
    all: "All of Pustaka Aceh",
    allWithCount: "All of Pustaka Aceh ({{count}} documents)",
    pustakaOff: "Pustaka Aceh is off",
    collections_one: "{{count}} collection",
    collections_other: "{{count}} collections",
    documents_one: "{{count}} document",
    documents_other: "{{count}} documents",
    empty: "No sources selected",
    emptyHelp: "Pick at least one collection or document. With no sources, nothing can be answered.",
    pickerTitle: "Choose sources",
    pickerSearch: "Search document titles…",
    pickerApply: "Apply",
    pickerCancel: "Cancel",
    selected: "Selected",
    clear: "Clear",
    sourceGroup: "Source family",
    // The rail groups sources by WHOSE they are, which is the question a reader
    // actually has when choosing what to search.
    groupCorpus: "Shared corpus",
    groupShared: "Shared",
    groupMine: "My collections",
    status: "Status",
    collection: "Collection",
    resultsFor: "{{shown}} of {{total}} documents",
    stamp: "Answered with scope: {{scope}}",
    stampUnknown: "The scope this answer ran under was not stored.",
  },
  chat: {
    placeholder: "Ask about Aceh government documents…",
    send: "Send",
    newChat: "New chat",
    stop: "Stop",
    hint: "Enter to send · Shift+Enter for a new line",
    waiting: "Composing the answer",
    waitingHelp:
      "The model reads documents, checks source support, then applies the policy filter. The answer appears once those finish.",
    elapsed: "{{seconds}}s",
    you: "Your question",
    stopped: "Stopped before the answer finished.",
    emptyTitle: "No questions yet",
    emptyBody: "Ask below. The answer will be built from the documents in your chosen scope.",
  },
  answer: {
    heading: "Answer",
    historyWindowTitle: "Earlier questions are out of view",
    historyWindowBody_one:
      "I can see {{count}} earlier question in this chat. Some older questions are out of view. If you refer to something earlier, restate it or start a new chat.",
    historyWindowBody:
      "I can see {{count}} earlier questions in this chat. Some older questions are out of view. If you refer to something earlier, restate it or start a new chat.",
    supported: "{{supported}} of {{total}} claims supported by sources",
    droppedNone: "Every claim is supported by a source",
    gateUnknown: "Source support is not fully verified for this answer.",
    // NOT "removed". The gate drops a claim from the CITED set; the sentence stays in
    // the answer, so "removed" described a deletion the reader could see had not
    // happened.
    dropped_one: "{{count}} claim is not supported by a source",
    dropped_other: "{{count}} claims are not supported by a source",
    unverified: "unsupported",
    lateGate: "The source check ran after the text was sent, so the marks arrive late.",
    fallbackOverRefusal:
      "The source check rejected too many claims, so this shorter answer is shown instead.",
    // The amber panel's TITLE. It used to reuse "Jawaban"/"Answer", which reads as
    // a heading for the answer rather than a warning about it.
    fallbackTitleNoClaims: "Not verified against a source",
    fallbackTitleUnverifiedNoClaims: "Answer withheld",
    fallbackTitleOverRefusal: "Most claims were unsupported",
    fallbackTitleCoverageIncomplete: "Source coverage is incomplete",
    fallbackTitleUnresolvedCitation: "Source reference could not be resolved",
    fallbackTitleUnparseableCompletion: "Answer format could not be verified",
    fallbackTitleAgentOutputLimit: "Answer too long to finish",
    fallbackTitleUnknown: "Unrecognized verification status",
    fallbackNoClaims:
      "This answer makes no checkable claim, so nothing in it has been verified against a source.",
    fallbackUnverifiedNoClaims:
      "The answer's statements could not be verified against the sources found, so the answer was not shown.",
    fallbackCoverageIncomplete:
      "The source-support records do not cover the complete answer, so the answer was not shown.",
    fallbackUnresolvedCitation:
      "The answer referenced a source that was unavailable to this check, so the answer was not shown.",
    fallbackUnparseableCompletion:
      "The model returned an invalid answer format, so its unverified content was not shown.",
    fallbackAgentOutputLimit:
      "The answer reached its length limit before it was finished, so it was not shown. Asking again gives the same result; ask about one part of the question instead.",
    partialTitle: "Partial answer",
    partialBody:
      "Only statements verified against a source are shown. The note at the end names the parts the documents could not answer.",
    fallbackUnknown:
      "The answer was downgraded for an unrecognized verification reason. Check its sources before relying on it.",
    guardBlocked: "Held by the Policy Filter",
    guardBody:
      "The question or answer does not meet government communication policy. Try rephrasing.",
    copy: "Copy",
    copied: "Copied",
    copyWithSources: "Copy with sources",
    print: "Print",
    like: "Helpful",
    dislike: "Not helpful",
    comment: "Add a note",
    sources: "Sources",
  },
  answerV2: {
    mode: "Answer mode",
    modeV2: "v2 mode (annotated)",
    modeV1: "v1 mode",
    modeLocked: "This notebook was answered in {{mode}}. Start a new notebook to use another mode.",
    verdict: {
      supported: "verified",
      unsupported: "not supported by source",
      unverified: "not yet verified",
      uncited: "no source",
      invalid_citation: "invalid citation",
      from_conversation: "from the conversation, not a document",
    },
    supportedTitle: "Verified: the cited source supports this statement.",
    unsupportedTitle: "Not supported: the cited source does not support this statement.",
    unverifiedTitle: "Not yet verified: this statement was not checked against its source.",
    unverifiedTitleReason: "Not yet verified ({{reason}}): this statement was not checked against its source.",
    reason: {
      deadline: "time ran out",
      budget: "check budget spent",
      judge_unavailable: "judge unavailable",
      unparseable: "judge verdict unreadable",
      disputed: "checks disagreed",
    },
    disputedTitle:
      "Not yet verified ({{reason}}): one check found the cited source does not support this statement; a second check found that it does.",
    uncitedTitle: "No source: this statement cites no document.",
    invalidCitationTitle: "Invalid citation: the cited source is not one that was read.",
    fromConversationTitle:
      "From the conversation, not a document: restates this conversation; no document confirms it.",
    bannerNotVerified_one: "{{count}} statement is not verified — check its source before using it.",
    bannerNotVerified_other:
      "{{count}} statements are not verified — check their sources before using them.",
    bannerFromConversation_one:
      "{{count}} statement comes from earlier in the conversation, not from a document.",
    bannerFromConversation_other:
      "{{count}} statements come from earlier in the conversation, not from a document.",
    bannerUnchecked: "This answer has not been checked.",
    bannerUncheckedBody:
      "No part of this answer is verified against a source. Check the sources before using it.",
    bannerNoClaims: "This answer contains no statement checked against a source.",
    bannerUnlocated: "Part of this answer's text is not tied to a checked statement and is not verified.",
    bannerNotFull: "This answer is not fully verified.",
    footSupported: "{{supported}} of {{total}} statements supported by sources",
    footUnchecked: "Not checked against sources",
    footNoClaims: "No factual statement to check",
    refusal: {
      query_budget_exhausted: {
        title: "Search limit reached",
        body: "The search reached its model-call limit before the answer was written. Narrow the question or the scope, then try again.",
      },
      context_budget_exhausted: {
        title: "Sources too large to answer from",
        body: "The sources this question needs do not fit the model's context, so the answer was stopped before it was written and nothing was checked. Narrow the scope or the question.",
      },
      model_timeout: {
        title: "Timed out",
        body: "The model did not finish answering in time. Try again shortly.",
      },
      mcp_source_unavailable: {
        title: "Live data source unavailable",
        body: "A live data source the question needs could not be reached, so no answer was written.",
      },
      source_out_of_scope: {
        title: "Outside the source scope",
        body: "This question needs sources outside this notebook's scope. Add the right sources, then try again.",
      },
      source_scope_empty: {
        title: "No indexed documents in this source",
        body: "The source you chose has no indexed documents yet, so this question cannot be answered from it. Choose another source, or wait until its documents finish indexing.",
      },
      source_scope_unavailable: {
        title: "Source scope unavailable",
        body: "This notebook's source scope cannot be checked right now, so no answer was written.",
      },
      unparseable_completion: {
        title: "Answer format could not be verified",
        body: "The model returned an invalid answer format, so unverified content is not shown.",
      },
    },
  },
  evidence: {
    title: "Basis for this answer",
    fromIndexed: "From indexed documents",
    fromLive: "From live data sources",
    liveReadNote:
      "Read live when the question was asked — not indexed. Open the source or re-run the call above to check the figure.",
    emptyTitle: "No basis yet",
    emptyBody: "The sources behind an answer appear here.",
    page: "p. {{page}}",
    open: "Open document",
    noText: "The source text is not available for this citation.",
    indexed: "Indexed",
    more: "Show more",
    less: "Show less",
    openDocument: "Open the source document",
    toolCall: "Tool call",
    toolArgs: "Arguments",
    toolHost: "Source",
    toolFetched: "Fetched",
    toolRows: "Rows",
    toolNote: "This tool answered from a live data source; the call is the citation.",
  },
  trace: {
    working: "Working…",
    trail: "Search trail",
    distinct_one: "{{count}} distinct call",
    distinct_other: "{{count}} distinct calls",
    guardBlocked: "held — the answer was not shown",
    guardPassed: "passed — the answer may be shown",
    repeatedHelp: "The agent repeated the identical call with identical arguments; the result was identical too.",
    title: "Agent steps",
    round: "Model call {{n}}",
    receipt_one: "{{count}} step",
    receipt_other: "{{count}} steps",
    show: "Show steps",
    hide: "Hide steps",
    searching: "searching documents",
    reading: "reading a spreadsheet",
    portal: "reading a data portal",
    done: "done",
  },
  reader: {
    title: "Source document",
    passage: "The cited passage",
    original: "Original file",
    absentTitle: "The original file is not in this environment",
    absentBody: "The document row exists, but its bytes are not stored here.",
    cannotEmbed: "The browser cannot display this file.",
    notRenderable: "This format cannot be displayed in the browser",
    download: "Download the file",
    landedPage: "Opened at page {{page}}",
    citedRange: "The highlighted passage is the cited range",
    // The honest fallback (#533): the file opens, but nothing marks the cited spot.
    // Saying so beats letting the top of the file pose as the citation target.
    noLocation: "The exact original location of this citation is unavailable; showing the document from the beginning",
  },
  collections: {
    title: "Collections",
    subtitle: "Group documents, upload files, and set what gets searched.",
    create: "New collection",
    createTitle: "Create a collection",
    name: "Name",
    namePlaceholder: "e.g. Draft Qanun 2026",
    description: "Description",
    descriptionPlaceholder: "Optional — what this collection is for.",
    orgUnit: "Organisational unit",
    orgUnitHelp: "Optional. Becomes the default for documents uploaded here.",
    positionLevel: "Minimum position level",
    confidential: "Mark as confidential",
    confidentialHelp:
      "Documents uploaded here are marked confidential by default. Access is enforced per document at retrieval time.",
    submit: "Create",
    submitting: "Creating…",
    cancel: "Cancel",
    nameTaken: "That collection name is taken.",
    delete: "Delete collection",
    deleteConfirm: "Delete “{{name}}”?",
    deleteBody:
      "Its documents are not deleted — they lose this collection's label and stay searchable.",
    deleteSubmit: "Delete",
    documentCount_one: "{{count}} document",
    documentCount_other: "{{count}} documents",
    documentCountZero: "No indexed documents yet",
    pendingNote: "Files still processing are not counted.",
    emptyTitle: "No collections yet",
    emptyBody: "Create one to group documents and control what gets searched.",
    adminOnly: "Only an administrator can delete a collection.",
    shared: "Shared corpus",
    sharedNote: "System-managed — not deletable from here.",
  },
  addSources: {
    title: "Add sources",
    wholeCorpus: "All of Pustaka Aceh",
    wholeCorpusSub: "{{total}} documents — including ones added later",
    collections: "Collections",
    documents: "Documents",
    alreadyAdded: "already added",
    noDocuments: "No documents match.",
    moreExist: "Showing {{shown}} of {{total}}. Narrow the search to see the rest.",
    add: "Add {{count}} sources",
  },
  notebook: {
    rename: "Click to rename",
    untitled: "Untitled notebook",
    namePlaceholder: "Notebook name",
    notFoundTitle: "Notebook not found",
    notFoundBody:
      "This notebook does not exist, or your account cannot access it. Start a new notebook to ask.",
    removeCollection: "Remove collection {{name}} from sources",
    moreInCollection: "Showing {{shown}} of {{total}} documents.",
    addSources: "+ Add sources",
    wholePustaka: "All of Pustaka Aceh · {{total}} documents",
    sources: "Sources",
    attachPustaka: "Add all of Pustaka ({{total}} documents)",
    detachPustaka: "Remove all of Pustaka",
    unfiled: "No collection",
    wholeCollection: "whole collection",
    liveCollection: "Everything in this collection is searched, including documents added later.",
    remove: "Remove",
    removeDocument: "Remove {{title}} from sources",
    excluded: "{{count}} excluded",
    moved: "{{count}} sources followed their document to a newer version.",
    missing: "{{count}} sources are no longer available.",
    empty: "No sources yet. Add a collection or documents before asking.",
  },
  library: {
    facetSearch: "Search…",
    facetNoMatch: 'Nothing matches "{{needle}}".',
    allRegions: "All regions",
    allYears: "All years",
    title: "Library",
    subtitle: "Every indexed document you can access.",
    search: "Search titles…",
    upload: "Upload documents",
    uploadTitle: "Upload documents",
    uploadDrop: "Drop files here, or click to choose",
    uploadFormats: "PDF · DOCX · XLSX · CSV · HTML · scanned images",
    uploadBrowse: "Choose files",
    uploadTo: "Add to collection",
    uploadNone: "No collection",
    uploadSourceType: "Source family",
    uploadSubmit: "Upload",
    uploadSubmitting: "Uploading…",
    uploadQueued: "{{count}} file(s) queued for processing.",
    uploadStatus: "Upload status",
    columns: {
      title: "Title",
      year: "Year",
      region: "Region",
      source: "Source family",
      format: "Format",
      status: "Status",
      collection: "Collection",
    },
    status: {
      pending: "Pending",
      processing: "Processing",
      indexed: "Indexed",
      needs_review: "Needs review",
      failed: "Failed",
      archived: "Archived",
    },
    ocrReading: "reading text (OCR)",
    emptyTitle: "No documents",
    emptyBody: "Nothing matches these filters.",
    total: "{{shown}} of {{total}} documents",
    more: "Load more",
  },
  collection: {
    documents: "Documents in this collection",
    addDocuments: "Add documents",
    inScope: "In scope",
    excluded: "Excluded",
    excludeHelp:
      "Excluding a document here applies to every conversation. To narrow one conversation only, use the scope picker.",
    emptyTitle: "This collection is empty",
    emptyBody: "Upload a file above, or add documents that are already in the library.",
    uploadHere: "Upload into {{name}}",
    addDocumentsTo: "Add documents to {{name}}",
    noCandidates: "No documents left to add.",
    alreadyFiled: "in another collection",
    picked: "{{count}} selected",
    addSubmit: "Add to collection",
    adding: "Adding…",
    addPartial: "{{filed}} added, {{failed}} could not be added.",
  },
  document: {
    showChunks: "Show chunks",
    hideChunks: "Hide chunks",
    page: "p. {{page}}",
    chunksShown: "Showing {{shown}} of {{total}} chunks.",
    searchable: "Searchable",
    chunkCount_one: "{{count}} chunk",
    chunkCount_other: "{{count}} chunks",
    noChunks: "No indexed chunks yet",
    openOriginal: "Open the original",
    openSource: "Open the source",
    notIndexedYet: "This document is not indexed yet, so it cannot be answered from.",
    jenis: "Type",
  },
  errors: {
    generic: "Something went wrong.",
    network: "Could not reach the service.",
    unauthorized: "Your session ended. Please sign in again.",
    notFound: "Not found.",
    queryNotFound: "Conversation not found, or there is no indexed content to answer from.",
    tooLarge: "Too much context to answer at once. Narrow the scope or the question.",
    upstream: "The model could not be reached. Try again shortly.",
    emptyQuery: "The question cannot be empty.",
    // Two cases behind one status, as with 404 — name both rather than guess.
    badRequest:
      "The question is empty, or this notebook no longer has any sources. Add a collection or a document, then try again.",
    retry: "Try again",
    v2Unavailable: "v2 mode is not enabled on this server",
    v2UnavailableBody: "This server has not enabled /query/v2. The answer mode is back on v1.",
    askWithV1: "Ask with v1 mode",
    versionConflict:
      "This conversation was answered in the other answer mode. Start a new notebook to use this mode.",
  },
  common: {
    loading: "Loading…",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    search: "Search",
    all: "All",
    none: "None",
    of: "of",
  },
};
